import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import LazyImage from './LazyImage';
import { beforeAfterProjects } from '../data/mockData';

const BeforeAfter = () => {
  const [activeProject, setActiveProject] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  // Use real data from mockData.js, or fallback to empty array
  const projects = beforeAfterProjects || [];

  const handleSliderChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setSliderPosition(percent);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(touch.clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setSliderPosition(percent);
  };

  const nextProject = () => {
    setActiveProject((prev) => (prev + 1) % projects.length);
    setSliderPosition(50);
  };

  const prevProject = () => {
    setActiveProject((prev) => (prev - 1 + projects.length) % projects.length);
    setSliderPosition(50);
  };

  // Don't render if no projects
  if (!projects || projects.length === 0) {
    return null;
  }

  const currentProject = projects[activeProject];

  return (
    <section className="py-16 bg-gradient-to-br from-amber-50 via-white to-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mb-4">
            לפני ואחרי
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-6 rounded-full"></div>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
            ראו בעצמכם את כוח השינוי - פרויקטים אמיתיים שלנו
          </p>
        </div>

        {/* Before/After Slider */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Image Container */}
            <div
              className="relative aspect-[4/3] overflow-hidden select-none cursor-ew-resize"
              onMouseMove={isDragging ? handleSliderChange : undefined}
              onMouseDown={() => setIsDragging(true)}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              onTouchMove={handleTouchMove}
              onTouchStart={() => setIsDragging(true)}
              onTouchEnd={() => setIsDragging(false)}
            >
              {/* After Image (Background) */}
              <div className="absolute inset-0">
                <img
                  src={currentProject.after}
                  alt={`אחרי - ${currentProject.title}`}
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                  אחרי
                </div>
              </div>

              {/* Before Image (Clipped) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
              >
                <img
                  src={currentProject.before}
                  alt={`לפני - ${currentProject.title}`}
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                  לפני
                </div>
              </div>

              {/* Slider Line */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-2xl cursor-ew-resize"
                style={{ left: `${sliderPosition}%` }}
              >
                {/* Slider Handle */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-2xl flex items-center justify-center border-4 border-amber-500">
                  <div className="flex items-center">
                    <ChevronRight className="w-4 h-4 text-amber-600" />
                    <ChevronLeft className="w-4 h-4 text-amber-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Project Info */}
            <div className="p-8 bg-gradient-to-br from-white to-amber-50">
              <div className="flex items-center justify-center">
                <h3 className="text-2xl md:text-3xl font-bold text-amber-900 text-center">
                  {currentProject.title}
                </h3>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-6 mt-8">
            <button
              onClick={prevProject}
              className="bg-white hover:bg-amber-50 text-amber-600 p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl"
              aria-label="פרויקט קודם"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dots */}
            <div className="flex gap-3">
              {projects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setActiveProject(index);
                    setSliderPosition(50);
                  }}
                  className={`transition-all duration-300 rounded-full ${
                    index === activeProject
                      ? 'w-12 h-4 bg-amber-500'
                      : 'w-4 h-4 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`עבור לפרויקט ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextProject}
              className="bg-white hover:bg-amber-50 text-amber-600 p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl"
              aria-label="פרויקט הבא"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
